import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from './Header';
import { API_URL, getImageUrl, normalizeList } from '../config';
import SellerBankKycFields, {
  emptySellerBankForm,
  isSellerBankFormStarted,
  validateSellerBankForm,
  appendSellerBankFormData,
} from './SellerBankKycFields';
import './AddSellerPage.css';
import './Auth.css';

/** Build selectable items at Sub, Tertiary, and Quaternary levels (tertiary/quaternary optional). */
const flattenCategorySelections = (categories, normalizeList) => {
  const items = [];
  normalizeList(categories).forEach((c) => {
    normalizeList(c?.subCategories).forEach((s) => {
      items.push({ type: 'sub', id: s.subCategoryId, path: `${c.categoryName} › ${s.subCategoryName}` });
      normalizeList(s?.tertiaryCategories).forEach((t) => {
        items.push({ type: 'tertiary', id: t.tertiaryCategoryId, path: `${c.categoryName} › ${s.subCategoryName} › ${t.tertiaryCategoryName}` });
        normalizeList(t?.quaternaryCategories).forEach((q) => {
          items.push({ type: 'quaternary', id: q.quaternaryCategoryId, path: `${c.categoryName} › ${s.subCategoryName} › ${t.tertiaryCategoryName} › ${q.quaternaryCategoryName}` });
        });
      });
    });
  });
  return items;
};

const AddSellerPage = () => {
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const userRole = useSelector((state) => state.auth.userRole);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    commissionPercent: '',
    shopName: '',
    gstNumber: '',
    ownerName: '',
    shopPhotoUrl: '',
    shopMapUrl: '',
    shopAddress: '',
    phoneNumber: '',
    alternatePhoneNumber: '',
    address: '',
    city: '',
    state: '',
    pinCode: '',
    location: ''
  });
  const [shopPhotoUploading, setShopPhotoUploading] = useState(false);
  const [shopPhotoPreview, setShopPhotoPreview] = useState('');
  const [bankForm, setBankForm] = useState(emptySellerBankForm());
  const [passbookFile, setPassbookFile] = useState(null);
  const [passbookPreview, setPassbookPreview] = useState('');
  const [subCategoryIds, setSubCategoryIds] = useState([]);
  const [tertiaryCategoryIds, setTertiaryCategoryIds] = useState([]);
  const [quaternaryCategoryIds, setQuaternaryCategoryIds] = useState([]);
  const [categoryTree, setCategoryTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successReferralCode, setSuccessReferralCode] = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/Categories/with-subcategories`)
      .then((res) => setCategoryTree(normalizeList(res.data)))
      .catch(() => setCategoryTree([]));
  }, []);

  // Redirect if not admin
  if (!isLoggedIn || userRole !== 'Admin') {
    navigate('/');
    return null;
  }

  const getAuthHeader = () => {
    const token = localStorage.getItem('jwtToken');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  const getApiErrorMessage = (err, fallback) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;
    if (typeof data?.title === 'string' && data.title.trim()) return data.title;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    if (err?.response?.status === 403) return 'You do not have permission to upload this file.';
    if (err?.response?.status === 401) return 'Please log in as Admin and try again.';
    return fallback;
  };

  const clearShopPhotoPreview = () => {
    setShopPhotoPreview((prev) => {
      if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const handleShopPhotoUpload = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    clearShopPhotoPreview();
    const localUrl = URL.createObjectURL(file);
    setShopPhotoPreview(localUrl);
    setShopPhotoUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_URL}/ImageUpload/shop`, fd, {
        headers: getAuthHeader()
      });
      const url = res.data?.imageUrl ?? '';
      setFormData(prev => ({ ...prev, shopPhotoUrl: url }));
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to upload shop photo.'));
    } finally {
      setShopPhotoUploading(false);
      e.target.value = '';
    }
  };

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.password) {
      setError('Password is required');
      return false;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    const commission = parseFloat(formData.commissionPercent);
    if (formData.commissionPercent !== '' && (isNaN(commission) || commission < 0 || commission > 100)) {
      setError('Commission % must be between 0 and 100');
      return false;
    }
    if (isSellerBankFormStarted(bankForm, passbookFile)) {
      const bankError = validateSellerBankForm(bankForm, passbookFile, false);
      if (bankError) {
        setError(bankError);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('jwtToken');
      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password
      };
      if (formData.commissionPercent !== '') payload.commissionPercent = parseFloat(formData.commissionPercent);
      if (formData.shopName?.trim()) payload.shopName = formData.shopName.trim();
      if (formData.gstNumber?.trim()) payload.gstNumber = formData.gstNumber.trim();
      if (formData.ownerName?.trim()) payload.ownerName = formData.ownerName.trim();
      if (formData.shopPhotoUrl?.trim()) payload.shopPhotoUrl = formData.shopPhotoUrl.trim();
      if (formData.shopMapUrl?.trim()) payload.shopMapUrl = formData.shopMapUrl.trim();
      if (formData.shopAddress?.trim()) payload.shopAddress = formData.shopAddress.trim();
      if (formData.phoneNumber?.trim()) payload.phoneNumber = formData.phoneNumber.trim();
      if (formData.alternatePhoneNumber?.trim()) payload.alternatePhoneNumber = formData.alternatePhoneNumber.trim();
      if (formData.address?.trim()) payload.address = formData.address.trim();
      if (formData.city?.trim()) payload.city = formData.city.trim();
      if (formData.state?.trim()) payload.state = formData.state.trim();
      if (formData.pinCode?.trim()) payload.pinCode = formData.pinCode.trim();
      if (formData.location?.trim()) payload.location = formData.location.trim();
      if (subCategoryIds.length > 0) payload.subCategoryIds = subCategoryIds;
      if (tertiaryCategoryIds.length > 0) payload.tertiaryCategoryIds = tertiaryCategoryIds;
      if (quaternaryCategoryIds.length > 0) payload.quaternaryCategoryIds = quaternaryCategoryIds;
      const res = await axios.post(`${API_URL}/Sellers`, payload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = res.data || {};
      const referralCode = data.referralCode ?? '';
      const createdSellerId = data.userId ?? data.sellerId;

      if (createdSellerId && isSellerBankFormStarted(bankForm, passbookFile)) {
        const bankFd = new FormData();
        appendSellerBankFormData(bankFd, bankForm, passbookFile, formData.ownerName.trim() || formData.username);
        await axios.post(`${API_URL}/Sellers/${createdSellerId}/bank-details`, bankFd, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      setSuccess(true);
      setSuccessReferralCode(referralCode);
      setShowSuccessPopup(true);
      setFormData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        commissionPercent: '',
        shopName: '',
        gstNumber: '',
        ownerName: '',
        shopPhotoUrl: '',
        shopMapUrl: '',
        shopAddress: '',
        phoneNumber: '',
        alternatePhoneNumber: '',
        address: '',
        city: '',
        state: '',
        pinCode: '',
        location: ''
      });
      setSubCategoryIds([]);
      setTertiaryCategoryIds([]);
      setQuaternaryCategoryIds([]);
      clearShopPhotoPreview();
      setBankForm(emptySellerBankForm());
      setPassbookFile(null);
      if (passbookPreview && passbookPreview.startsWith('blob:')) URL.revokeObjectURL(passbookPreview);
      setPassbookPreview('');

    } catch (err) {
      setError(err.response?.data || 'Failed to create seller. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessOk = () => {
    setShowSuccessPopup(false);
    setSuccessReferralCode('');
    navigate('/admin/sellers');
  };

  return (
    <div className="add-seller-wrapper">
      {showSuccessPopup && (
        <div className="auth-snackbar-overlay" role="dialog" aria-label="Seller created successfully">
          <div className="auth-snackbar">
            <p className="auth-snackbar-title">Seller created successfully!</p>
            <p className="auth-snackbar-message">
              {successReferralCode
                ? <>Referral Code (for login): <strong>{successReferralCode}</strong></>
                : <>Seller has been created successfully.</>}
            </p>
            <p className="auth-snackbar-note">A welcome email has been sent to the seller&apos;s email address.</p>
            <button type="button" className="auth-snackbar-ok" onClick={handleSuccessOk}>OK</button>
          </div>
        </div>
      )}
      <Header />
      <div className="add-seller-container">
        <div className="add-seller-card">
          <h2>Add New Seller</h2>
          <p className="subtitle">Create a new seller account</p>

          {success && !showSuccessPopup && (
            <div className="success-message">
              Seller created successfully!
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="seller-form">
            <div className="form-group">
              <label htmlFor="username">Username *</label>
              <input
                type="text"
                id="username"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Enter username"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter email address"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password *</label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Enter password (min 6 characters)"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm password"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="commissionPercent">Commission % (Admin receives)</label>
              <input
                type="number"
                id="commissionPercent"
                name="commissionPercent"
                value={formData.commissionPercent}
                onChange={handleChange}
                placeholder="e.g. 10 (0-100)"
                min="0"
                max="100"
                step="0.5"
                disabled={loading}
              />
              <small className="form-hint">Percentage of seller&apos;s transaction value that admin will receive</small>
            </div>

            <h3 className="form-section-title">Shop &amp; seller details (fill maximum details)</h3>
            <div className="form-row-two">
              <div className="form-group">
                <label htmlFor="shopName">Shop name</label>
                <input type="text" id="shopName" name="shopName" value={formData.shopName} onChange={handleChange} placeholder="e.g. ABC Store" disabled={loading} />
              </div>
              <div className="form-group">
                <label htmlFor="gstNumber">GST number</label>
                <input type="text" id="gstNumber" name="gstNumber" value={formData.gstNumber} onChange={handleChange} placeholder="e.g. 12AAAAA0000A1Z5" disabled={loading} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="ownerName">Owner name</label>
              <input type="text" id="ownerName" name="ownerName" value={formData.ownerName} onChange={handleChange} placeholder="Full name of shop owner" disabled={loading} />
            </div>
            <SellerBankKycFields
              values={bankForm}
              onChange={setBankForm}
              passbookFile={passbookFile}
              passbookPreview={passbookPreview}
              existingPassbookUrl=""
              holderName={formData.ownerName.trim() || formData.username}
              disabled={loading}
              onPassbookChange={(e) => {
                const file = e.target?.files?.[0];
                if (!file) return;
                if (file.size > 5 * 1024 * 1024) {
                  setError('Passbook file must be 5MB or smaller.');
                  return;
                }
                if (passbookPreview && passbookPreview.startsWith('blob:')) URL.revokeObjectURL(passbookPreview);
                setPassbookFile(file);
                setPassbookPreview(URL.createObjectURL(file));
              }}
            />
            <div className="form-group">
              <label>Shop photo</label>
              <div className="upload-area-inline">
                <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleShopPhotoUpload} disabled={loading || shopPhotoUploading} />
                {(shopPhotoPreview || formData.shopPhotoUrl) ? (
                  <div className="preview-inline">
                    <img src={shopPhotoPreview || getImageUrl(formData.shopPhotoUrl)} alt="Shop preview" />
                    <span className="uploaded-tag">{shopPhotoUploading ? 'Uploading…' : (formData.shopPhotoUrl ? 'Uploaded' : 'Preview')}</span>
                  </div>
                ) : (
                  <span className="upload-hint">Click to upload shop photo (JPG, PNG)</span>
                )}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="shopMapUrl">Shop map URL</label>
              <input
                type="text"
                id="shopMapUrl"
                name="shopMapUrl"
                value={formData.shopMapUrl}
                onChange={handleChange}
                placeholder="Paste Google Maps link"
                disabled={loading}
              />
              <small className="form-hint">Open Google Maps, share the shop location, and paste the link here.</small>
            </div>
            <div className="form-group">
              <label htmlFor="shopAddress">Shop address</label>
              <textarea id="shopAddress" name="shopAddress" value={formData.shopAddress} onChange={handleChange} placeholder="Full shop address" rows={2} disabled={loading} />
            </div>
            <div className="form-row-two">
              <div className="form-group">
                <label htmlFor="phoneNumber">Contact number</label>
                <input type="tel" id="phoneNumber" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="e.g. 9876543210" disabled={loading} />
              </div>
              <div className="form-group">
                <label htmlFor="alternatePhoneNumber">Alternate contact number</label>
                <input type="tel" id="alternatePhoneNumber" name="alternatePhoneNumber" value={formData.alternatePhoneNumber} onChange={handleChange} placeholder="Optional" disabled={loading} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="location">Location (area / locality)</label>
              <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} placeholder="e.g. MG Road, Sector 18" disabled={loading} />
            </div>
            <div className="form-group">
              <label htmlFor="address">Address line (street, building)</label>
              <input type="text" id="address" name="address" value={formData.address} onChange={handleChange} placeholder="Street, building" disabled={loading} />
            </div>
            <div className="form-row-three">
              <div className="form-group">
                <label htmlFor="city">City</label>
                <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} placeholder="City" disabled={loading} />
              </div>
              <div className="form-group">
                <label htmlFor="state">State</label>
                <input type="text" id="state" name="state" value={formData.state} onChange={handleChange} placeholder="State" disabled={loading} />
              </div>
              <div className="form-group">
                <label htmlFor="pinCode">Pin code</label>
                <input type="text" id="pinCode" name="pinCode" value={formData.pinCode} onChange={handleChange} placeholder="e.g. 110001" disabled={loading} />
              </div>
            </div>

            <div className="form-group categories-seller">
              <label>Categories this seller can sell in</label>
              <small className="form-hint">Select at SubCategory, Tertiary, or Quaternary level. Tertiary and Quaternary are optional — you can assign at SubCategory only.</small>
              <div className="quaternary-checkboxes">
                {flattenCategorySelections(categoryTree, normalizeList).map((item) => {
                  const checked = item.type === 'sub' ? subCategoryIds.includes(item.id)
                    : item.type === 'tertiary' ? tertiaryCategoryIds.includes(item.id)
                      : quaternaryCategoryIds.includes(item.id);
                  const setter = item.type === 'sub' ? setSubCategoryIds : item.type === 'tertiary' ? setTertiaryCategoryIds : setQuaternaryCategoryIds;
                  const typeLabel = item.type === 'sub' ? 'Sub' : item.type === 'tertiary' ? 'Tert' : 'Quat';
                  return (
                    <label key={`${item.type}-${item.id}`} className="quaternary-check">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setter(prev => [...prev, item.id]);
                          } else {
                            setter(prev => prev.filter(id => id !== item.id));
                          }
                        }}
                      />
                      <span className="quaternary-path"><span className="category-type-badge">{typeLabel}</span> {item.path}</span>
                    </label>
                  );
                })}
                {flattenCategorySelections(categoryTree, normalizeList).length === 0 && (
                  <p className="no-categories-msg">No categories yet. Add them under Admin → Manage Categories.</p>
                )}
              </div>
            </div>

            <div className="button-group">
              <button 
                type="button" 
                className="cancel-btn"
                onClick={() => navigate('/admin/sellers')}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="submit-btn"
                disabled={loading}
              >
                {loading ? 'Creating...' : 'Create Seller'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddSellerPage;
